/**
 * @file     LeadCaptureModal.tsx
 * @purpose  The question, documents and PDF listing sheets, opened through
 *           LeadCaptureContext from every listing surface. Each is one sheet on
 *           the shared ListingSheet shell: signed-out visitors give first name,
 *           last name, email and mobile phone above the single consent line;
 *           signed-in visitors skip the fields because the route files the
 *           request on their account.
 *             - inquiry: topics plus "Your question"
 *             - documents: the checklist and the fields together
 *             - pdf: the lead, then /api/property-pdf streams the file to this
 *               device. The route does not email a copy, so the sheet never
 *               says it does.
 *           Posts to /api/property-inquiries, which calls recordLead, so every
 *           request lands a `leads` row and reaches the CRM. New accounts get
 *           their session in place; existing ones are never signed in.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useFormMemory } from '@/hooks/useFormMemory';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useAnalytics } from '@/hooks/useAnalytics';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { analyticsEvents } from '@/core/constants/analytics';
import { useWishlist } from '@/components/properties/useWishlist';
import { gateColors } from '@/components/gate/GateSheet';
import { ContactFields } from '@/components/lead-capture/ContactFields';
import { AntiSpamHoneypot } from '@/components/forms/AntiSpamHoneypot';
import { useAntiSpam } from '@/services/anti-spam';
import { useConsentChoices } from '@/components/lead-capture/ConsentChoices';
import { RealtorQuestion } from '@/components/lead-capture/RealtorQuestion';
import {
  ListingSheet,
  SheetChip,
  SheetError,
  SheetFooter,
  SheetLabel,
  SheetSent,
  SHEET_ICONS,
} from '@/components/lead-capture/ListingSheet';
import { contactPayload, validateContact, type ContactErrors, type ContactField, type ContactValues } from '@/services/leads/contactFields';
import { layoutTokens } from '@/design-system/tokens';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { useLeadCapture, type OpenPayload, type RequestedDocument } from './LeadCaptureContext';
import { gatedSecureFetch, phoneGateFailure } from '@/services/phone-gate/gatedFetch';

type Phase = 'form' | 'submitting' | 'sent';
type AuthKind = 'signed_in' | 'new_account' | 'existing_account';

const EMPTY: ContactValues = { firstName: '', lastName: '', email: '', phone: '' };

/** Question topics: one tap adds the subject to the message. */
const TOPICS = [
  { id: 'fees', key: 'listingSheet.question.topic.fees', label: 'Fees and taxes' },
  { id: 'parking', key: 'listingSheet.question.topic.parking', label: 'Parking' },
  { id: 'pets', key: 'listingSheet.question.topic.pets', label: 'Pets' },
  { id: 'offers', key: 'listingSheet.question.topic.offers', label: 'Offer date' },
] as const;

const DEFAULT_MESSAGE = 'I am interested in this property and would like more information.';

function listingKeyOf(payload: OpenPayload): string {
  return payload.listing.listingMlsNumber ?? payload.listing.listingSlug;
}

function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function LeadCaptureModal() {
  const { setHoneypotValue, getAntiSpamMetadata } = useAntiSpam();
  const { isOpen, payload, close } = useLeadCapture();
  const { t } = useTranslation();
  const { mode: colorMode } = useAppTheme();
  const { isAuthenticated } = useAuth();
  const { trackEvent } = useAnalytics();
  const { remove } = useWishlist();
  const { getMemory } = useFormMemory();

  const [contact, setContact] = useState<ContactValues>(EMPTY);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [message, setMessage] = useState('');
  const [topics, setTopics] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [phase, setPhase] = useState<Phase>('form');
  const [failed, setFailed] = useState(false);
  const [failCopy, setFailCopy] = useState<string | null>(null);
  const consent = useConsentChoices();
  // Documents only: asked before a request that could imply agency.
  const [realtor, setRealtor] = useState<boolean | null>(null);
  const [realtorError, setRealtorError] = useState(false);
  const [authKind, setAuthKind] = useState<AuthKind | null>(null);
  const [priorInquiry, setPriorInquiry] = useState(false);
  // PDF: the lead is filed once; a failed download retries only the file.
  const [leadFiled, setLeadFiled] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfFailed, setPdfFailed] = useState(false);
  const openedRef = useRef<OpenPayload | null>(null);
  const sentRef = useRef(false);

  const mode = payload?.mode ?? 'inquiry';
  const options: RequestedDocument[] = payload?.documentOptions ?? payload?.requestedDocuments ?? [];

  useEffect(() => {
    if (!isOpen || !payload) return;
    if (openedRef.current === payload) return;
    openedRef.current = payload;
    sentRef.current = false;
    const mem = getMemory();
    const [first = '', ...rest] = (mem.name ?? '').trim().split(/\s+/);
    setContact(isAuthenticated ? EMPTY : { firstName: first, lastName: rest.join(' '), email: mem.email ?? '', phone: mem.phone ?? '' });
    setErrors({});
    setMessage('');
    setTopics([]);
    setSelected(new Set((payload.requestedDocuments ?? options).map((d) => d.id)));
    setPhase('form');
    setFailed(false);
    consent.reset();
    setRealtor(null);
    setRealtorError(false);
    setAuthKind(null);
    setPriorInquiry(false);
    setLeadFiled(false);
    setPdfFailed(false);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    const listingKey = listingKeyOf(payload);
    const surface = payload.analyticsSurface ?? 'unknown';
    if (payload.mode === 'inquiry') {
      reltaTrack('inquiry.open', { listing_key: listingKey });
    }
    if (payload.mode === 'documents') {
      const count = (payload.documentOptions ?? payload.requestedDocuments ?? []).length;
      reltaTrack('documents.request.open', { listing_key: listingKey, document_count: Math.max(1, count) });
      trackEvent({
        eventName: analyticsEvents.propertyDocumentRequestOpened,
        eventType: 'click',
        category: 'property_detail',
        label: surface,
        metadata: { listing_key: listingKey, surface, document_count: count },
      });
    }
    if (payload.mode === 'inquiry' && isAuthenticated) {
      fetch(`/api/property-inquiries?listingKey=${encodeURIComponent(payload.listing.listingSlug)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data: { inquiry: { message: string | null } | null } | null) => {
          if (openedRef.current !== payload || !data?.inquiry) return;
          setPriorInquiry(true);
          setMessage(data.inquiry.message ?? '');
        })
        .catch(() => {});
    }
    // Once per opened payload; the payload carries everything the open needs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, payload]);

  useEffect(() => {
    if (!isOpen) openedRef.current = null;
  }, [isOpen]);

  if (!payload) return null;

  const listingKey = listingKeyOf(payload);
  const surface = payload.analyticsSurface ?? 'unknown';
  const showFields = !isAuthenticated;
  const pending = phase === 'submitting';
  const chosen = options.filter((d) => selected.has(d.id));

  const dismiss = () => {
    if (pending) return;
    if (!sentRef.current) {
      if (mode === 'inquiry') reltaTrack('inquiry.abandon', { listing_key: listingKey, step: 'sheet' });
      if (mode === 'documents') {
        const count = Math.max(1, chosen.length);
        reltaTrack('documents.request.abandon', { listing_key: listingKey, document_count: count });
        trackEvent({
          eventName: analyticsEvents.propertyDocumentRequestAbandoned,
          eventType: 'behavior',
          category: 'property_detail',
          label: surface,
          metadata: { listing_key: listingKey, surface, document_count: chosen.length },
        });
      }
    }
    close();
  };

  const pdfName = `relta-${payload.listing.listingMlsNumber ?? payload.listing.listingSlug}.pdf`;

  const fetchPdf = async (): Promise<boolean> => {
    setPdfFailed(false);
    try {
      const { secureFetch } = await import('@/services/anti-scraping/secureFetch');
      const res = await secureFetch('/api/property-pdf', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ listingSlug: payload.listing.listingSlug, colorMode }),
      });
      if (!res.ok) throw new Error(`pdf ${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      setPdfUrl(url);
      triggerDownload(url, pdfName);
      return true;
    } catch {
      setPdfFailed(true);
      return false;
    }
  };

  const submit = async () => {
    if (pending) return;
    if (mode === 'documents' && chosen.length === 0) return;
    const asksRealtor = mode === 'documents';
    const found = showFields ? validateContact(contact, t) : {};
    if (showFields) setErrors(found);
    if (asksRealtor) setRealtorError(realtor === null);
    if (Object.keys(found).length > 0 || (asksRealtor && realtor === null)) return;
    setFailed(false);
    setFailCopy(null);
    setPhase('submitting');

    if (mode === 'pdf' && leadFiled) {
      const ok = await fetchPdf();
      setPhase(ok ? 'sent' : 'form');
      return;
    }

    try {
      const { secureFetch } = await import('@/services/anti-scraping/secureFetch');
      const topicText = topics
        .map((id) => TOPICS.find((topic) => topic.id === id)?.label)
        .filter(Boolean)
        .join(', ');
      const question = [topicText ? `Topics: ${topicText}.` : '', message.trim()].filter(Boolean).join('\n');
      const { listingPlace: _place, listingThumb: _thumb, ...listing } = payload.listing;
      const res = await gatedSecureFetch('/api/property-inquiries', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          mode: mode === 'documents' ? 'inquiry' : mode,
          requestKind: mode === 'documents' ? 'documents' : undefined,
          requestedDocuments: mode === 'documents' ? chosen : undefined,
          listing,
          contact: showFields ? contactPayload(contact) : null,
          antiSpam: getAntiSpamMetadata(),
          ...(showFields ? consent.payload(contact.phone) : {}),
          message: mode === 'inquiry' ? question || DEFAULT_MESSAGE : undefined,
          ...(asksRealtor ? { workingWithRealtor: realtor } : {}),
          intent: priorInquiry ? 'edit' : 'new',
        }),
      });
      if (!res.ok) {
        const why = await phoneGateFailure(res);
        if (why === 'unconfirmed') setFailCopy(t('listingSheet.phone.unconfirmed', { defaultValue: 'Your number was not confirmed, so nothing was sent. Confirm it and send again.' }));
        else if (why === 'rate_limited') setFailCopy(t('listingSheet.phone.rateLimited', { defaultValue: 'Too many codes were sent to this number. Wait a few minutes, then try again.' }));
        else if (why === 'invalid_phone') setFailCopy(t('listingSheet.phone.invalid', { defaultValue: 'That number cannot receive a code. Check it and try again.' }));
        throw new Error(`inquiry ${res.status}`);
      }
      const data = (await res.json()) as { ok: boolean; kind: AuthKind; session?: { access_token: string; refresh_token: string } };
      if (data.session && data.kind === 'new_account') {
        const { createSupabaseBrowserClient } = await import('@/services/supabase/client');
        await createSupabaseBrowserClient().auth.setSession(data.session);
      }
      sentRef.current = true;
      setAuthKind(data.kind);
      payload.onSubmitted?.(asksRealtor ? realtor === true : undefined);

      if (mode === 'documents') {
        reltaTrack('documents.request.submit', { listing_key: listingKey, document_ids: chosen.map((d) => d.id), document_count: chosen.length });
        trackEvent({
          eventName: analyticsEvents.propertyDocumentRequestSubmitted,
          eventType: 'conversion',
          category: 'property_detail',
          label: surface,
          metadata: { listing_key: listingKey, surface, document_count: chosen.length, working_with_realtor: realtor },
        });
      }
      if (mode === 'inquiry' && data.kind !== 'existing_account') {
        // Asked about it: the home leaves the liked list (LikedTabControls listens).
        const key = payload.listing.listingSlug;
        remove({ source: payload.listing.listingSource, listingKey: key } as never);
        try {
          const existing = JSON.parse(localStorage.getItem('relta_inquired_keys') ?? '[]') as string[];
          if (!existing.includes(key)) {
            localStorage.setItem('relta_inquired_keys', JSON.stringify([...existing, key]));
            window.dispatchEvent(new StorageEvent('storage', { key: 'relta_inquired_keys' }));
          }
        } catch { /* storage disabled: the liked list just keeps the card */ }
      }
      if (mode === 'pdf') {
        setLeadFiled(true);
        const ok = await fetchPdf();
        setPhase(ok ? 'sent' : 'form');
        return;
      }
      setPhase('sent');
    } catch {
      setFailed(true);
      setPhase('form');
    }
  };

  const seeSimilar = payload.onSeeSimilar
    ? {
        label: t('listingActions.label.seeSimilar', { defaultValue: 'See similar homes' }),
        onClick: () => {
          close();
          payload.onSeeSimilar?.();
        },
      }
    : null;
  const backToListing = { label: t('listingSheet.backToListing', { defaultValue: 'Back to listing' }), icon: SHEET_ICONS.back, onClick: close };

  const copy = {
    inquiry: {
      title: t('listingSheet.question.title', { defaultValue: 'Ask about this home' }),
      next: t('listingSheet.question.next', { defaultValue: 'Ray Rasouli will reply by email or phone.' }),
      cta: t('listingSheet.question.submit', { defaultValue: 'Send question' }),
      action: t('forms.consent.sending', { defaultValue: 'sending' }),
      sentTitle: t('listingSheet.question.sent', { defaultValue: 'Question sent' }),
      sentLine: t('listingSheet.question.next', { defaultValue: 'Ray Rasouli will reply by email or phone.' }),
    },
    documents: {
      title: t('listingSheet.documents.title', { defaultValue: 'Request documents' }),
      next: t('listingSheet.documents.next', { defaultValue: 'Ray Rasouli will email what the listing brokerage shares.' }),
      cta: t('listingSheet.documents.submit', { defaultValue: 'Request documents' }),
      action: t('forms.consent.requesting', { defaultValue: 'requesting' }),
      sentTitle: t('listingSheet.sent', { defaultValue: 'Request sent' }),
      sentLine: t('listingSheet.documents.sentLine', { defaultValue: 'The documents come by email as the listing brokerage shares them.' }),
    },
    pdf: {
      title: t('listingSheet.pdf.title', { defaultValue: 'Save this listing as a PDF' }),
      next: '',
      cta: t('listingSheet.pdf.submit', { defaultValue: 'Get the PDF' }),
      action: t('forms.consent.continuing', { defaultValue: 'continuing' }),
      sentTitle: t('listingSheet.pdf.sent', { defaultValue: 'Your PDF is ready' }),
      sentLine: '',
    },
  }[mode];

  const sheetListing = {
    title: payload.listing.listingTitle,
    price: payload.listing.listingPrice,
    place: payload.listing.listingPlace,
    thumbUrl: payload.listing.listingThumb,
  };

  return (
    <ListingSheet
      open={isOpen}
      onClose={dismiss}
      listing={sheetListing}
      title={phase === 'sent' ? copy.sentTitle : copy.title}
      titleHidden={phase === 'sent'}
      stepKey={phase === 'sent' ? 'sent' : 'form'}
      testId={`listing-sheet-${mode}`}
    >
      {phase === 'sent' ? (
        <SheetSent
          title={copy.sentTitle}
          line={copy.sentLine || undefined}
          primary={
            mode === 'pdf' && pdfUrl
              ? { label: t('listingSheet.pdf.download', { defaultValue: 'Download PDF' }), icon: SHEET_ICONS.download, onClick: () => triggerDownload(pdfUrl, pdfName) }
              : backToListing
          }
          secondary={seeSimilar}
        >
          {authKind === 'existing_account' ? (
            <Typography component="p" sx={{ m: 0, fontSize: 14, color: gateColors.textSecondary }}>
              {t('listingSheet.existingAccount', { defaultValue: 'Sign in with this email to see it in your dashboard.' })}
            </Typography>
          ) : null}
        </SheetSent>
      ) : (
        <>
          {mode === 'inquiry' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box role="group" aria-label={t('listingSheet.question.topics', { defaultValue: 'Topics' })} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {TOPICS.map((topic) => (
                  <SheetChip
                    key={topic.id}
                    pressed={topics.includes(topic.id)}
                    onToggle={() => setTopics((prev) => (prev.includes(topic.id) ? prev.filter((id) => id !== topic.id) : [...prev, topic.id]))}
                  >
                    {t(topic.key, { defaultValue: topic.label })}
                  </SheetChip>
                ))}
              </Box>
              <Box component="label" htmlFor="listing-question" sx={{ display: 'block', mt: 1, mb: -1, fontSize: 14, fontWeight: 500, color: 'text.secondary' }}>
                {t('listingSheet.question.label', { defaultValue: 'Your question' })}
              </Box>
              <TextField
                id="listing-question"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
                multiline
                minRows={3}
                fullWidth
                disabled={pending}
              />
            </Box>
          ) : null}

          {mode === 'documents' ? (
            <Box role="group" aria-label={t('listingSheet.documents.group', { defaultValue: 'Documents' })} component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {options.map((doc) => {
                const checked = selected.has(doc.id);
                return (
                  <Box component="li" key={doc.id} sx={{ borderBottom: `1px solid ${gateColors.border}` }}>
                    <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 48, cursor: 'pointer' }}>
                      <Checkbox
                        checked={checked}
                        disabled={pending}
                        onChange={() =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (next.has(doc.id)) next.delete(doc.id);
                            else next.add(doc.id);
                            return next;
                          })
                        }
                        sx={{ ml: -2, color: gateColors.borderStrong, '&.Mui-checked': { color: gateColors.primary } }}
                      />
                      <Box component="span" sx={{ fontSize: 15, fontWeight: fontWeightTokens.medium }}>{doc.label}</Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}

          {mode === 'pdf' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3.5, p: 3, borderRadius: `${layoutTokens.radius.sm}px`, backgroundColor: gateColors.surfaceSunken }}>
              <Box
                aria-hidden
                sx={{
                  flexShrink: 0,
                  width: 52,
                  height: 68,
                  boxSizing: 'border-box',
                  p: 1.5,
                  borderRadius: '8px',
                  backgroundColor: gateColors.surface,
                  border: `1px solid ${gateColors.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Box sx={{ height: 22, borderRadius: '3px', backgroundColor: gateColors.chip }} />
                {['80%', '60%', '70%'].map((w) => (
                  <Box key={w} sx={{ height: 3, width: w, borderRadius: '2px', backgroundColor: gateColors.borderStrong }} />
                ))}
              </Box>
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography component="p" sx={{ m: 0, fontSize: 15, fontWeight: fontWeightTokens.semibold }}>
                  {t('listingSheet.pdf.card', { defaultValue: 'Listing summary PDF' })}
                </Typography>
                <Typography component="p" sx={{ m: 0, fontSize: 13.5, color: gateColors.textSecondary }}>
                  {t('listingSheet.pdf.cardLine', { defaultValue: 'It downloads to this device.' })}
                </Typography>
              </Box>
            </Box>
          ) : null}

          <AntiSpamHoneypot onValueChange={setHoneypotValue} />
          {showFields && !(mode === 'pdf' && leadFiled) ? (
            <ContactFields
              values={contact}
              errors={errors}
              onChange={(field: ContactField, value: string) => {
                setContact((prev) => ({ ...prev, [field]: value }));
                setErrors((prev) => ({ ...prev, [field]: undefined }));
              }}
              disabled={pending}
              idPrefix={`listing-${mode}`}
            />
          ) : null}

          {mode === 'documents' ? (
            <RealtorQuestion
              value={realtor}
              onChange={setRealtor}
              yesLine={t('forms.realtor.yesDocuments', { defaultValue: 'The REALTOR® you work with can also get these for you.' })}
              error={realtorError}
              disabled={pending}
              idPrefix="listing-documents"
            />
          ) : null}

          {pdfFailed ? (
            <SheetError>{t('listingSheet.pdf.failed', { defaultValue: 'The PDF did not load. Try again in a moment.' })}</SheetError>
          ) : null}

          <SheetFooter
            next={copy.next || undefined}
            cta={copy.cta}
            pending={pending}
            disabled={mode === 'documents' && chosen.length === 0}
            error={failed ? failCopy ?? true : false}
            onSubmit={() => void submit()}
            consent={showFields && !(mode === 'pdf' && leadFiled) ? { action: copy.action } : null}
            consentChoices={showFields && !(mode === 'pdf' && leadFiled) ? { values: consent.values, onChange: consent.setValues, phone: true } : null}
          />
        </>
      )}
    </ListingSheet>
  );
}
